import {memoize} from '@packages/utilities/memo';
import {Expressions, Variable, matchExpression} from '@packages/systems/wfdl';
import {constant, constantIdentity} from '@packages/utilities/fp/utils';
import {type ExpressionElement, EElement} from '@packages/systems/wfdl-ast';
import {
  isLocalesWrapper,
  isLocalesList,
} from '@packages/utilities/elementTypeUtils';
import {localesListItemVariable, localesListActiveItemVariable} from '../types';

type ContainsLocalesBindingsResult = {
  localesListItem?: boolean;
  localesListActiveItem?: boolean;
  orphanLocalesList?: boolean;
};

const isLocalesListItemVariable = Variable.equals(localesListItemVariable);
const isLocalesListActiveItemVariable = Variable.equals(
  localesListActiveItemVariable
);

export const containsLocalesBindings = memoize((from: ExpressionElement) => {
  // we are going to mutate the bindings object instead of implementing a
  // function to concatenate results for the Expressions.everything
  // because we can safely stop the whole walking if there is a true localesListItem
  const bindings: ContainsLocalesBindingsResult = {};
  const stop = Expressions.stop(bindings);
  const resume = Expressions.resume(bindings);
  const expressionMatcher = matchExpression({
    // stop walking down the tree if the needed context is there
    Element: (element) => {
      if (isLocalesWrapper(element)) {
        return stop;
      }

      if (isLocalesList(element)) {
        bindings.orphanLocalesList = true;
        return stop;
      }

      return resume;
    },
    Variable: (value) => {
      // stop if localesListItem is true since a LocalesItem
      // will always be nested in a LocalesWrapper
      if (isLocalesListItemVariable(value)) {
        bindings.localesListItem = true;
        return stop;
      }

      // resume since there can be a more restrictive localesListItem
      if (isLocalesListActiveItemVariable(value)) {
        bindings.localesListActiveItem = true;
        return resume;
      }

      return resume;
    },
    _: constant(resume),
  });

  return Expressions.everything<ContainsLocalesBindingsResult>(
    constantIdentity
  )((expression) =>
    bindings.localesListItem ? stop : expressionMatcher(expression)
  )(EElement(from));
});
